/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Card } from '@mui/material';
import { isEqual } from 'lodash';
import React from 'react';
import type { PlotPreset, TimeSeriesService } from '@opengeoweb/shared';
import { AlertBanner } from '@opengeoweb/shared';
import type { TimeSliderStartEndStep } from '@opengeoweb/store';
import { PLOT_HEIGHT } from './chartUtils';
import { TimeSeriesChart } from './TimeSeriesChart';
import type {
  PointerLocation,
  GeoJsonWithService,
  FailedParameterInfo,
} from './types';
import { useTimeseriesTranslation } from '../../utils/i18n';
import { useGetPlotsWithData } from '../../utils/timeseries-api/hooks';

const getParameterKey = (param: FailedParameterInfo): string => {
  return `${param.serviceId}|${param.collectionId}|${param.propertyName}`;
};

const PlotErrorBanners: React.FC<{
  plotsWithData: {
    plotId: string;
    errors?: string[];
    failedParameters?: FailedParameterInfo[];
  }[];
  parametersFailingInAllPlots: FailedParameterInfo[];
  legendHeight: number;
  getErrorTitle: (parameters: FailedParameterInfo[]) => string;
  getErrorTranslationKey: (errorKey: string, parameterCount: number) => string;
  t: (key: string) => string;
}> = ({
  plotsWithData,
  parametersFailingInAllPlots,
  legendHeight,
  getErrorTitle,
  getErrorTranslationKey,
  t,
}) => {
  return (
    <>
      {plotsWithData.map((plot, index) => {
        const parametersFailingKeys = new Set(
          parametersFailingInAllPlots.map(getParameterKey),
        );
        const plotSpecificFailedParameters =
          plot.failedParameters?.filter(
            (param) => !parametersFailingKeys.has(getParameterKey(param)),
          ) || [];

        if (plotSpecificFailedParameters.length === 0) {
          return null;
        }

        const errorTitle = getErrorTitle(plotSpecificFailedParameters);

        const errorKey = plot.errors?.[0] || 'error-message-general';
        const translationKey = getErrorTranslationKey(
          errorKey,
          plotSpecificFailedParameters.length,
        );
        const errorInfo = t(translationKey);

        return (
          <Box
            key={`error-${plot.plotId}`}
            sx={{
              position: 'absolute',
              top: legendHeight + PLOT_HEIGHT * index + 35,
              left: 0,
              right: 0,
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            <Box sx={{ pointerEvents: 'auto' }}>
              <AlertBanner info={errorInfo} shouldClose title={errorTitle} />
            </Box>
          </Box>
        );
      })}
    </>
  );
};

interface TimeSeriesViewProps {
  plotPreset: PlotPreset;
  selectedLocation: PointerLocation;
  services: TimeSeriesService[];
  splitGeoJsonByCollection: GeoJsonWithService[];
  isLoading?: (startOrFinish: boolean) => void;
  shouldShowLegend?: boolean;
  onToggleLegendParam?: (legendId: string) => void;
  syncgroupTimeState?: TimeSliderStartEndStep;
}

export const TimeSeriesView: React.FC<TimeSeriesViewProps> = React.memo(
  ({
    plotPreset,
    selectedLocation,
    services,
    splitGeoJsonByCollection,
    isLoading,
    shouldShowLegend = true,
    onToggleLegendParam,
    syncgroupTimeState,
  }: TimeSeriesViewProps) => {
    const { t } = useTimeseriesTranslation();
    const [legendHeight, setLegendHeight] = React.useState(0);
    const { data: plotsWithData, isFetching: isFetchingPlots } =
      useGetPlotsWithData(
        plotPreset,
        selectedLocation,
        services,
        splitGeoJsonByCollection,
        t,
        syncgroupTimeState,
      );

    React.useEffect(() => {
      if (isLoading) {
        isLoading(isFetchingPlots);
      }
    }, [isFetchingPlots, isLoading]);

    const getErrorTitle = (parameters: FailedParameterInfo[]): string => {
      const propertyNames = parameters.map((param) => param.propertyName);
      if (propertyNames.length > 1) {
        return `"${propertyNames.join('", "')}"`;
      }
      return `"${propertyNames[0] || ''}"`;
    };

    const getErrorTranslationKey = (
      errorKey: string,
      parameterCount: number,
    ): string => {
      // Use plural translation only when multiple parameters fail with error-message-general
      return errorKey === 'error-message-general' && parameterCount > 1
        ? 'error-message-general-plural'
        : errorKey;
    };

    const getCombinedErrorInfo = React.useCallback(
      (
        parametersFailingInAllPlots: FailedParameterInfo[],
        plotsWithData: { errors?: string[] }[] | undefined,
        totalPlotCount: number,
      ): string => {
        if (parametersFailingInAllPlots.length === 0 || totalPlotCount === 0) {
          return '';
        }

        const errorKey =
          plotsWithData?.[0]?.errors?.[0] || 'error-message-general';
        const translationKey = getErrorTranslationKey(
          errorKey,
          parametersFailingInAllPlots.length,
        );
        const errorMessage = t(translationKey);

        return totalPlotCount === 1
          ? errorMessage
          : `${t('all-plots')}: ${errorMessage}`;
      },
      [t],
    );

    const getParametersFailingInAllPlots = (
      plotsWithData: { failedParameters?: FailedParameterInfo[] }[] | undefined,
      totalPlotCount: number,
    ): FailedParameterInfo[] => {
      if (!plotsWithData || totalPlotCount === 0) {
        return [];
      }

      const parameterCounts: Record<
        string,
        { count: number; param: FailedParameterInfo }
      > = {};
      plotsWithData.forEach((plot) => {
        plot.failedParameters?.forEach((param) => {
          const key = getParameterKey(param);
          if (parameterCounts[key]) {
            parameterCounts[key].count += 1;
          } else {
            parameterCounts[key] = { count: 1, param };
          }
        });
      });

      return Object.values(parameterCounts)
        .filter(({ count }) => count === totalPlotCount)
        .map(({ param }) => param);
    };

    const totalPlotCount = plotsWithData?.length || 0;
    const parametersFailingInAllPlots: FailedParameterInfo[] = React.useMemo(
      () => getParametersFailingInAllPlots(plotsWithData, totalPlotCount),
      [plotsWithData, totalPlotCount],
    );

    const hasParametersFailingInAllPlots =
      parametersFailingInAllPlots.length > 0;
    const combinedErrorTitle = getErrorTitle(parametersFailingInAllPlots);
    const combinedErrorInfo = React.useMemo(
      () =>
        hasParametersFailingInAllPlots
          ? getCombinedErrorInfo(
              parametersFailingInAllPlots,
              plotsWithData,
              totalPlotCount,
            )
          : '',
      [
        getCombinedErrorInfo,
        hasParametersFailingInAllPlots,
        parametersFailingInAllPlots,
        plotsWithData,
        totalPlotCount,
      ],
    );

    return (
      <Box data-testid="TimeSeriesView">
        {hasParametersFailingInAllPlots ? (
          <Card
            sx={{
              border: 'solid 1px',
              borderColor: 'geowebColors.cards.cardContainerBorder',
              boxShadow: 'none',
            }}
          >
            <Box data-testid="TimeSeriesView-alert-banner">
              <AlertBanner
                info={combinedErrorInfo}
                shouldClose
                title={combinedErrorTitle}
              />
            </Box>
          </Card>
        ) : null}
        {!!plotsWithData && plotsWithData.length > 0 && (
          <Card
            sx={{
              border: 'solid 1px',
              borderColor: 'geowebColors.cards.cardContainerBorder',
              boxShadow: 'none',
              position: 'relative',
            }}
          >
            {!!plotsWithData && plotsWithData.length > 0 && (
              <PlotErrorBanners
                getErrorTitle={getErrorTitle}
                getErrorTranslationKey={getErrorTranslationKey}
                legendHeight={legendHeight}
                parametersFailingInAllPlots={parametersFailingInAllPlots}
                plotsWithData={plotsWithData}
                t={t}
              />
            )}
            <TimeSeriesChart
              onLegendHeightChange={setLegendHeight}
              onToggleLegendParam={onToggleLegendParam}
              plotsWithData={plotsWithData}
              shouldShowLegend={shouldShowLegend}
            />
          </Card>
        )}
      </Box>
    );
  },
  (previousProps, nextProps) => {
    const samePreset = previousProps.plotPreset === nextProps.plotPreset;
    const sameLocation = isEqual(
      previousProps.selectedLocation,
      nextProps.selectedLocation,
    );
    const sameGeoJson = isEqual(
      previousProps.splitGeoJsonByCollection,
      nextProps.splitGeoJsonByCollection,
    );
    const sameLegend =
      previousProps.shouldShowLegend === nextProps.shouldShowLegend;
    const sameServices = isEqual(previousProps.services, nextProps.services);
    const sameTime =
      previousProps.syncgroupTimeState?.start ===
        nextProps.syncgroupTimeState?.start &&
      previousProps.syncgroupTimeState?.end ===
        nextProps.syncgroupTimeState?.end;

    return (
      samePreset &&
      sameLocation &&
      sameGeoJson &&
      sameLegend &&
      sameServices &&
      sameTime
    );
  },
);
